import { InputNumber } from 'antd';
import { memo, useCallback, useEffect, useState } from 'react';
import type { MetricColumnConfig } from '../../../config/tpoGenConfig';

interface EditableMetricCellProps {
    value: number;
    config: MetricColumnConfig;
    onCommit: (newValue: number) => void;
    showEditableIndicator?: boolean;
}

const formatDisplay = (value: number | undefined, config: MetricColumnConfig): string => {
    if (value === undefined || value === null) return '-';
    const formatted = config.allowedDecimals > 0 ? value.toFixed(config.allowedDecimals) : String(value);
    return `${config.preUnit}${formatted}${config.postUnit}`;
};

const EditableMetricCell = memo(
    ({ value, config, onCommit, showEditableIndicator = false }: EditableMetricCellProps) => {
        const [editing, setEditing] = useState(false);
        const [localValue, setLocalValue] = useState<number | null>(value);
        const [warning, setWarning] = useState('');

        useEffect(() => {
            setLocalValue(value);
        }, [value]);

        const validate = useCallback(
            (val: number): boolean => {
                if (config.minValue !== null && val < config.minValue) {
                    setWarning(`Min value is ${config.minValue}`);
                    return false;
                }
                if (config.maxValue !== null && val > config.maxValue) {
                    setWarning(`Max value is ${config.maxValue}`);
                    return false;
                }
                setWarning('');
                return true;
            },
            [config],
        );

        const commitValue = useCallback(() => {
            setEditing(false);
            setWarning('');

            if (localValue === null || localValue === undefined) {
                setLocalValue(value);
                return;
            }

            if (localValue === value) return;

            if (!validate(localValue)) {
                setLocalValue(value);
                return;
            }

            onCommit(localValue);
        }, [localValue, value, validate, onCommit]);

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                    commitValue();
                } else if (e.key === 'Escape') {
                    setLocalValue(value);
                    setEditing(false);
                    setWarning('');
                }
            },
            [commitValue, value],
        );

        if (!editing) {
            return (
                <span
                    className={`cursor-pointer text-sm text-[#18181B] hover:bg-blue-50 px-1 py-0.5 transition-colors min-w-[60px] inline-block${
                        showEditableIndicator ? ' border-b border-dashed border-gray-300' : ''
                    }`}
                    onClick={() => setEditing(true)}
                >
                    {formatDisplay(value, config)}
                </span>
            );
        }

        return (
            <div className="flex flex-col">
                <InputNumber
                    autoFocus
                    size="small"
                    value={localValue}
                    onChange={(val) => setLocalValue(val)}
                    onBlur={commitValue}
                    onKeyDown={handleKeyDown}
                    precision={config.allowedDecimals}
                    min={config.minValue ?? undefined}
                    max={config.maxValue ?? undefined}
                    controls={false}
                    className="w-full"
                    status={warning ? 'error' : undefined}
                    style={{ width: 100 }}
                />
                {warning && <span className="text-xs text-red-500 mt-0.5">{warning}</span>}
            </div>
        );
    },
);

EditableMetricCell.displayName = 'EditableMetricCell';

export default EditableMetricCell;
